import { useState, useEffect, useCallback } from 'react';
import { m, AnimatePresence } from 'framer-motion';

interface ScrollToTopProps {
  /** Show button after scrolling this many pixels. Default: 400 */
  showAfter?: number;
  /** Position from bottom in pixels. Default: 24 */
  bottom?: number;
  /** Position from right in pixels. Default: 24 */
  right?: number;
}

/**
 * Back to Top button that appears after scrolling down.
 * Includes smooth scroll behavior and keyboard accessibility.
 */
export function ScrollToTop({
  showAfter = 400,
  bottom = 24,
  right = 24,
}: ScrollToTopProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > showAfter);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial position

    return () => window.removeEventListener('scroll', handleScroll);
  }, [showAfter]);

  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  return (
    <AnimatePresence>
      {isVisible && (
        <m.button
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 20 }}
          transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
          onClick={scrollToTop}
          style={{ bottom, right }}
          className="fixed z-40 p-3 bg-black text-white border-2 border-white/20 shadow-lg hover:bg-white hover:text-black hover:border-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
          aria-label="맨 위로 이동"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </m.button>
      )}
    </AnimatePresence>
  );
}
